import React, { useState } from 'react';
import Header from '@/components/Header';
import { useWhatsAppPopup } from '@/contexts/WhatsAppPopupContext';
import Footer from '@/components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Anchor, GraduationCap, LifeBuoy, Ship, ArrowRight, Wind, Waves } from 'lucide-react';
import HabilitacoesSection from '@/components/escola/HabilitacoesSection';
import DadosMercadoSection from '@/components/escola/DadosMercadoSection';
import GallerySection from '@/components/escola/GallerySection';

// Re-using assets from the project
import trainingClass from '@/assets/training-class.jpg';
import yachtHelm from '@/assets/yacht-helm.jpg';
import cardCaptain from '@/assets/card-captain.jpg';
import yachtInterior from '@/assets/yacht-interior.jpg';

// --- 1. Hero — Opção B: Split com Stats ---
const stats = [
  { number: '2.500+', label: 'Alunos formados' },
  { number: '98%', label: 'Aprovação no exame' },
  { number: '7', label: 'Anos de experiência' },
  { number: '4.9', label: 'Avaliação Google' },
];

const EscolaHero: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  return (
    <section className="relative bg-primary overflow-hidden">

      {/* Atmospheric orb */}
      <div
        className="absolute top-1/2 left-1/3 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, hsl(43 74% 53% / 0.07) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[520px]">

          {/* Left — Copy */}
          <div className="flex flex-col justify-center py-20 lg:pr-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5 mb-7 w-fit"
            >
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse flex-shrink-0" />
              <span className="text-gold text-xs font-semibold tracking-widest uppercase">Matrículas Abertas</span>
            </motion.div>

            {/* H1 */}
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.2 }}
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-primary-foreground leading-[1.1] mb-5"
            >
              Do Sonho à{' '}
              <em className="text-gold">Realidade</em>{' '}
              no Mar
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.3 }}
              className="text-primary-foreground/65 text-lg leading-relaxed max-w-md mb-9"
            >
              Sua habilitação náutica sem burocracia. Aprenda com quem vive o mar e conquiste a liberdade de navegar em até 30 dias.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
            >
              <button onClick={() => openPopup("escola")} className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium w-full sm:w-auto justify-center">
                Falar com Consultor <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#cursos"
                className="text-primary-foreground/50 hover:text-primary-foreground text-sm font-medium transition-colors duration-200 underline underline-offset-4"
              >
                Conhecer os cursos ↓
              </a>
            </motion.div>
          </div>

          {/* Right — Stats grid */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col justify-center py-12 lg:py-20 lg:pl-16 border-t border-white/[0.08] lg:border-t-0 lg:border-l lg:border-white/[0.08]"
          >
            <div
              className="grid grid-cols-2 rounded-2xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)', gap: '1px', background: 'rgba(255,255,255,0.08)' }}
            >
              {stats.map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.4 + idx * 0.08 }}
                  className="flex flex-col gap-2 px-8 py-9"
                  style={{ background: 'hsl(222 47% 11%)' }}
                >
                  <span className="font-heading text-4xl text-gold leading-none">{stat.number}</span>
                  <span className="text-primary-foreground/50 text-sm leading-snug">{stat.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

// --- 2. "Why JL?" Section (Mantida) ---
const WhyJLSection: React.FC = () => {
  const benefits = [
    {
      icon: <Anchor className="w-10 h-10 text-gold" />,
      title: "Metodologia Prática",
      description: "Aprenda navegando em embarcações modernas. Nossas aulas focam na experiência real que você precisa para se sentir seguro no comando."
    },
    {
      icon: <GraduationCap className="w-10 h-10 text-gold" />,
      title: "Instrutores Comandantes",
      description: "Seja treinado por quem vive o mar. Nossos instrutores são profissionais experientes, prontos para compartilhar conhecimento prático e real."
    },
    {
      icon: <LifeBuoy className="w-10 h-10 text-gold" />,
      title: "Assessoria Completa",
      description: "Cuidamos de tudo para você, da matrícula à emissão do seu documento. Sua única preocupação é aprender a navegar."
    }
  ];

  return (
    <section className="py-24 sm:py-32 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-primary">Navegue Sem Preocupações</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">Por que escolher a JL Escola Náutica para sua jornada?</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefits.map(benefit => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5 }}
              className="text-center p-8"
            >
              {benefit.icon}
              <h3 className="text-xl font-bold text-primary mt-4 mb-2">{benefit.title}</h3>
              <p className="text-muted-foreground">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};


// --- 3. Nossas Formações — Premium dark section ---

const AnchorSVG: React.FC = () => (
  <svg
    width="160" height="160" viewBox="0 0 160 160" fill="none"
    className="text-gold animate-float"
    style={{ filter: 'drop-shadow(0 0 18px hsl(43 74% 53% / 0.45))' }}
  >
    {/* Ring */}
    <circle cx="80" cy="24" r="13" stroke="currentColor" strokeWidth="3.5" />
    {/* Shaft */}
    <line x1="80" y1="37" x2="80" y2="134" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
    {/* Crossbar */}
    <line x1="42" y1="60" x2="118" y2="60" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
    {/* Crossbar end caps */}
    <circle cx="42" cy="60" r="5" fill="currentColor" />
    <circle cx="118" cy="60" r="5" fill="currentColor" />
    {/* Left fluke */}
    <path d="M80 134 C62 126 44 114 38 98 C34 88 42 82 50 88" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    {/* Right fluke */}
    <path d="M80 134 C98 126 116 114 122 98 C126 88 118 82 110 88" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    {/* Crown arc connecting fluke bases */}
    <path d="M50 88 Q80 98 110 88" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

const WavesSVG: React.FC = () => (
  <svg
    width="160" height="160" viewBox="0 0 160 160" fill="none"
    className="text-gold animate-float"
    style={{ filter: 'drop-shadow(0 0 18px hsl(43 74% 53% / 0.45))' }}
  >
    {/* Wave 1 */}
    <path d="M6 48 Q36 24 66 48 Q96 72 126 48 Q143 36 154 42" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    {/* Wave 2 */}
    <path d="M6 82 Q36 58 66 82 Q96 106 126 82 Q143 70 154 76" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    {/* Wave 3 */}
    <path d="M6 116 Q36 92 66 116 Q96 140 126 116 Q143 104 154 110" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    {/* Speed lines */}
    <line x1="104" y1="22" x2="150" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 3" opacity="0.55" />
    <line x1="112" y1="36" x2="154" y2="26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 3" opacity="0.55" />
    <line x1="96" y1="10" x2="134" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 4" opacity="0.35" />
  </svg>
);

const HelmSVG: React.FC = () => {
  const cx = 80, cy = 80;
  const outerR = 66, hubR = 16, spokeInR = 18, spokeOutR = 60, knobR = 6;
  const spokes = Array.from({ length: 8 }, (_, i) => {
    const a = (i * 45 * Math.PI) / 180;
    return {
      x1: cx + spokeInR * Math.cos(a), y1: cy + spokeInR * Math.sin(a),
      x2: cx + spokeOutR * Math.cos(a), y2: cy + spokeOutR * Math.sin(a),
      kx: cx + outerR * Math.cos(a), ky: cy + outerR * Math.sin(a),
    };
  });
  return (
    <svg
      width="160" height="160" viewBox="0 0 160 160" fill="none"
      className="text-gold animate-float"
      style={{ filter: 'drop-shadow(0 0 18px hsl(43 74% 53% / 0.45))' }}
    >
      <circle cx={cx} cy={cy} r={outerR} stroke="currentColor" strokeWidth="4" />
      {spokes.map((s, i) => (
        <g key={i}>
          <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <circle cx={s.kx} cy={s.ky} r={knobR} stroke="currentColor" strokeWidth="2.5" fill="hsl(222 47% 11% / 0.9)" />
        </g>
      ))}
      <circle cx={cx} cy={cy} r={hubR} stroke="currentColor" strokeWidth="3" />
      <circle cx={cx} cy={cy} r={6} fill="currentColor" />
    </svg>
  );
};

const DetailedCoursesSection: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  const [activeIdx, setActiveIdx] = useState(0);

  const courses = [
    {
      id: 'arrais',
      label: 'Arrais Amador',
      tagline: 'A LIBERDADE DE COMANDAR',
      title: 'Arrais Amador',
      level: 'Iniciante',
      skills: [
        { icon: <Anchor className="w-5 h-5" />, title: 'Manobras com Confiança', desc: 'Atraque, desatraque e ancore com precisão, mesmo com vento e correnteza. Sem medo de marinas movimentadas.' },
        { icon: <Waves className="w-5 h-5" />, title: 'Navegação Inteligente', desc: 'Regras de trânsito do mar, preferências e comunicação com outras embarcações — tudo que você precisa para navegar com segurança.' },
        { icon: <LifeBuoy className="w-5 h-5" />, title: 'Ação em Emergências', desc: 'Homem ao mar, combate a incêndio e socorro. Navegar seguro é navegar tranquilo.' },
      ],
      experience: 'Nossas aulas são realizadas em lanchas modernas. Você não vai apenas "cumprir horas" — vai realizar manobras reais em cenários reais, sob supervisão direta de um comandante experiente.',
      cta: 'Quero me tornar Arrais Amador',
      svg: <AnchorSVG />,
    },
    {
      id: 'motonauta',
      label: 'Motonauta',
      tagline: 'ADRENALINA COM CONTROLE TOTAL',
      title: 'Motonauta',
      level: 'Iniciante',
      skills: [
        { icon: <Wind className="w-5 h-5" />, title: 'Pilotagem Avançada', desc: 'Domine curvas e alta velocidade com controle total. Surpreenda-se com o que você e a máquina são capazes.' },
        { icon: <GraduationCap className="w-5 h-5" />, title: 'Regras e Normas', desc: 'Jet-skis têm regras próprias. Saiba onde navegar, limites de velocidade e normas para evitar multas e acidentes.' },
        { icon: <LifeBuoy className="w-5 h-5" />, title: 'Resgate e Auto-resgate', desc: 'Jet-ski capotado, pessoa na água, primeiros socorros — você estará pronto para qualquer situação.' },
      ],
      experience: 'Com nossa frota de jet-skis novos e instrutores especializados, sua aula será pura adrenalina e aprendizado em condições reais.',
      cta: 'Quero Dominar o Jet-Ski',
      svg: <WavesSVG />,
    },
    {
      id: 'mestre',
      label: 'Mestre Amador',
      tagline: 'NAVEGUE SEM FRONTEIRAS',
      title: 'Mestre Amador',
      level: 'Intermediário',
      skills: [
        { icon: <Ship className="w-5 h-5" />, title: 'Navegação Costeira', desc: 'Planeje e execute viagens longas com cartas náuticas, GPS e navegação estimada.' },
        { icon: <GraduationCap className="w-5 h-5" />, title: 'Meteorologia e Eletrônica', desc: 'Interprete boletins, desvie de mau tempo e opere radar, AIS e equipamentos avançados.' },
        { icon: <Anchor className="w-5 h-5" />, title: 'Mar Aberto', desc: 'Navegação noturna, comunicações e procedimentos de segurança para longas travessias costeiras.' },
      ],
      experience: 'Ministrado por Capitães com milhares de milhas navegadas. Preparação densa e suporte contínuo para que você esteja pronto não só para a prova, mas para o oceano.',
      cta: 'Quero Navegar em Mar Aberto',
      svg: <HelmSVG />,
    },
  ];

  const active = courses[activeIdx];

  return (
    <section id="cursos" className="py-24 sm:py-32 bg-background relative overflow-hidden">
      {/* Dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.045) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Atmospheric orb */}
      <div
        className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, hsl(43 74% 53% / 0.10) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-gold text-xs font-semibold tracking-widest uppercase">Formações</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-4">
            Nossas Formações
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Cursos desenhados para cada etapa da sua jornada no mar.
          </p>
        </motion.div>

        {/* Pills */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex justify-center mb-12"
        >
          <div className="flex gap-2 p-1.5 rounded-full bg-primary/5 border border-gold/20">
            {courses.map((course, idx) => (
              <button
                key={course.id}
                onClick={() => setActiveIdx(idx)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${activeIdx === idx
                    ? 'bg-gold text-primary'
                    : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                  }`}
              >
                {course.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Course content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.32 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-5xl mx-auto"
          >
            {/* Left: SVG decorativo */}
            <div className="relative flex items-center justify-center order-1 lg:order-none">
              <div
                className="absolute w-72 h-72 rounded-full pointer-events-none"
                style={{ background: 'hsl(43 74% 53% / 0.12)', filter: 'blur(48px)' }}
              />
              <div className="relative bg-primary/5 border border-gold/25 rounded-2xl p-14 flex items-center justify-center">
                {active.svg}
              </div>
            </div>

            {/* Right: Details */}
            <div className="flex flex-col gap-5">
              <span className="text-gold text-xs font-semibold tracking-widest uppercase">
                {active.tagline}
              </span>
              <h3 className="font-heading text-3xl sm:text-4xl text-primary leading-tight">
                {active.title}
              </h3>

              {/* Level badge */}
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/5 border border-primary/10 text-muted-foreground text-xs font-medium">
                  {active.level}
                </span>
              </div>

              {/* Skills */}
              <div className="space-y-4">
                {active.skills.map((skill, i) => (
                  <motion.div
                    key={skill.title}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.32 }}
                    className="flex items-start gap-4"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gold/10 flex items-center justify-center text-gold">
                      {skill.icon}
                    </div>
                    <div>
                      <p className="text-primary font-semibold text-sm">{skill.title}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed">{skill.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Callout */}
              <div className="bg-gold/5 border border-gold/20 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <Anchor className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-primary font-semibold text-sm mb-1">Experiência Prática JL</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{active.experience}</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={() => openPopup("escola")}
                className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium w-full sm:w-fit"
              >
                {active.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-12">
          {courses.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIdx(idx)}
              className={`rounded-full transition-all duration-200 ${activeIdx === idx ? 'bg-gold w-6 h-2' : 'bg-primary/15 w-2 h-2 hover:bg-primary/30'
                }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};



// --- 5. Simplified How It Works Section (Mantida) ---
const HowItWorksSimpleSection: React.FC = () => (
  <section className="py-24 sm:py-32 bg-primary/5">
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="font-heading text-3xl sm:text-4xl text-primary">Seu Caminho para a Habilitação</h2>
        <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">Um processo simples e transparente em 4 passos.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[{ n: 1, t: "Matrícula" }, { n: 2, t: "Aulas Práticas" }, { n: 3, t: "Prova na Marinha" }, { n: 4, t: "Habilitação em Mãos" }].map(step => (
          <div key={step.n} className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-gold text-primary-foreground flex items-center justify-center font-bold text-2xl mb-4">{step.n}</div>
            <h3 className="font-semibold text-lg text-primary">{step.t}</h3>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- 6. FAQ Section (Mantida e Melhorada) ---
const FaqEnhancedSection: React.FC = () => {
  const faqItems = [
    { question: "Preciso ter meu próprio barco para fazer as aulas?", answer: "Não. A JL Escola Náutica fornece toda a infraestrutura, incluindo embarcações modernas e seguras para suas aulas práticas. Você não precisa ter nenhuma experiência prévia." },
    { question: "A prova da Marinha é muito difícil?", answer: "A prova teórica de Arrais Amador tem 40 questões e exige 50% de acertos. Com nossa metodologia testada, simulados e instrutores dedicados, nossos alunos alcançam uma taxa de aprovação de 98%, garantindo resultados excepcionais." },
    { question: "Em quanto tempo eu consigo minha habilitação?", answer: "A aula prática obrigatória de Arrais dura, em média, 1 dia. Após a aprovação na prova teórica (agendada na Capitania dos Portos), o processo de emissão da CHA leva de 30 a 60 dias. Cuidamos de toda a burocracia para você." },
    { question: "Quais são os pré-requisitos?", answer: "Você precisa ter 18 anos completos na data do exame escrito e apresentar um atestado médico que comprove aptidão física e mental (pode ser substituído pela CNH válida em alguns casos). Podemos auxiliar com isso." },
    { question: "Posso fazer o curso de Arrais e Motonauta juntos?", answer: "Sim! O combo Arrais + Motonauta é o pacote mais vendido e oferece o melhor custo-benefício para iniciantes. A habilitação de Motonauta pode ser averbada na mesma carteira do Arrais." },
    { question: "O curso teórico pode ser feito à distância (EAD)?", answer: "Para Arrais Amador e Motonauta, a parte prática é obrigatória e presencial — a Marinha exige comprovação de horas embarcadas. Já para Mestre e Capitão Amador, a preparação teórica pode ser feita por autoestudo, e nós oferecemos todo o suporte necessário." },
    { question: "Qual a diferença entre Arrais, Mestre e Capitão Amador?", answer: "O Arrais navega em águas abrigadas (baías, rios e lagos). O Mestre pode navegar na costa brasileira (até 20 milhas). O Capitão Amador não tem limite de distância — pode fazer travessias oceânicas. Cada nível exige um curso e prova específicos, com complexidade crescente." },
    { question: "Quanto tempo vale a minha carteira de Arrais?", answer: "A Carteira de Habilitação de Amador (CHA) tem validade de 10 anos e é renovável sem necessidade de nova prova (para amadores até 65 anos). Nosso serviço de despachante também cuida da renovação quando chegar a hora." },
  ];

  return (
    <section className="bg-primary py-24 sm:py-32">
      <div className="container mx-auto px-6 lg:px-12">
        <h2 className="text-center font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground mb-16">Dúvidas Comuns</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b-gold/20">
                <AccordionTrigger className="text-left text-lg text-primary-foreground/90 hover:no-underline hover:text-gold">{item.question}</AccordionTrigger>
                <AccordionContent className="text-primary-foreground/80 text-base">{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};


// --- Final Page Component ---
const EscolaPage: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  return (
    <>
      <Header />
      <main>
        <EscolaHero />
        <WhyJLSection />
        <HabilitacoesSection />
        <DadosMercadoSection />
        <DetailedCoursesSection />
        <GallerySection />
        <HowItWorksSimpleSection />
        <FaqEnhancedSection />

        {/* Final CTA Section */}
        <section className="py-24 bg-background border-t border-border/50">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                Realize seu Sonho
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-6">
                Não espere mais para ser o <br /> <span className="text-gold italic">Comandante</span> do seu próprio lazer
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
                Junte-se a mais de 2.500 alunos formados e descubra por que somos a escola náutica mais bem avaliada da região. O mar está chamando.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <button onClick={() => openPopup("escola")} className="btn-gold text-xl px-12 py-8 rounded-lg shadow-xl hover:shadow-gold/20 transform hover:-translate-y-1 transition-all">
                  Matricular agora via WhatsApp
                </button>
              </div>
              <p className="mt-6 text-sm text-muted-foreground/60 flex items-center justify-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Instrutores online para tirar suas dúvidas
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default EscolaPage;
